import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  getFirestore, 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs,
  addDoc,
  doc,
  updateDoc,
  increment,
  deleteDoc
} from 'firebase/firestore';
import { useAuth } from '../../auth/AuthContext';
import UserAvatar from '../../images/user-avatar-32.png';
import { createNotification } from '../../utils/notifications';

function ForumEntry({ postId, post, hasUpvoted, onUpvote }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [commentCount, setCommentCount] = useState(post.commentCount || 0);
  const contentRef = useRef(null);
  const { user } = useAuth();
  const db = getFirestore();

  const [replyTo, setReplyTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [commentUpvotes, setCommentUpvotes] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleReply = (commentId, authorName) => {
    setReplyTo({ id: commentId, authorName });
    setReplyContent(`@${authorName} `);
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!user || !replyContent.trim() || !replyTo) return;

    try {
      const replyData = {
        postId,
        parentCommentId: replyTo.id,
        content: replyContent,
        authorId: user.uid,
        fullName: user.fullName || user.displayName || 'Anonymous', // Fallback to displayName
        authorPhotoURL: user.photoURL,
        timestamp: new Date(),
        upvotes: 0
      };

      await addDoc(collection(db, 'comments'), replyData);
      setReplyTo(null);
      setReplyContent('');
      
      // Refresh comments
      const commentsRef = collection(db, 'comments');
      const commentsQuery = query(
        commentsRef,
        where('postId', '==', postId),
        orderBy('timestamp', 'desc')
      );
      const snapshot = await getDocs(commentsQuery);
      setComments(snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })));
    } catch (error) {
      console.error('Error adding reply:', error);
    }
  };

  const handleCommentUpvote = async (commentId) => {
    if (!user) return;

    try {
      const commentRef = doc(db, 'comments', commentId);
      const upvotesRef = collection(db, 'commentUpvotes');
      const hasUpvoted = commentUpvotes[commentId];

      if (hasUpvoted) {
        await updateDoc(commentRef, {
          upvotes: increment(-1)
        });
        // Remove upvote
        const q = query(upvotesRef, 
          where('userId', '==', user.uid),
          where('commentId', '==', commentId)
        );
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          await deleteDoc(doc(db, 'commentUpvotes', snapshot.docs[0].id));
        }
      } else {
        await updateDoc(commentRef, {
          upvotes: increment(1)
        });
        // Add upvote
        await addDoc(upvotesRef, {
          userId: user.uid,
          commentId,
          timestamp: new Date()
        });
      }

      setCommentUpvotes(prev => ({
        ...prev,
        [commentId]: !hasUpvoted
      }));

    } catch (error) {
      console.error('Error updating comment upvote:', error);
    }
  };

  useEffect(() => {
    if (contentRef.current) {
      const isContentOverflowing = contentRef.current.scrollHeight > 192; // 48px * 4 (max-h-48)
      setIsOverflowing(isContentOverflowing);
    }
  }, [post.content]);

  useEffect(() => {
    const fetchComments = async () => {
      if (!postId) return;

      try {
        const commentsRef = collection(db, 'comments');
        const commentsQuery = query(
          commentsRef,
          where('postId', '==', postId),
          orderBy('timestamp', 'desc')
        );
        const commentsSnapshot = await getDocs(commentsQuery);
        const commentsData = commentsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setComments(commentsData);
      } catch (error) {
        console.error('Error fetching comments:', error);
      }
    };

    fetchComments();
  }, [postId, db]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    if (!user || !newComment.trim()) return;

    try {
      const commentData = {
        postId,
        content: newComment,
        authorId: user.uid,
        fullName: user.fullName || user.displayName || 'Anonymous', // Fallback to displayName
        authorPhotoURL: user.photoURL,
        timestamp: new Date(),
        upvotes: 0
      };

      await addDoc(collection(db, 'comments'), commentData);
      
      // Create a notification for the post author
      if (post.authorId && post.authorId !== user.uid) { // Prevent notifying self
        await createNotification(postId, post.authorId, `${user.fullName || user.displayName || 'Anonymous'} har kommenteret dit opslag.`);
      }

      // Update post comment count
      const postRef = doc(db, 'posts', postId);
      await updateDoc(postRef, {
        commentCount: increment(1)
      });

      setNewComment('');
      
      // Refresh comments and update count
      const commentsRef = collection(db, 'comments');
      const commentsQuery = query(
        commentsRef,
        where('postId', '==', postId),
        orderBy('timestamp', 'desc')
      );
      const commentsSnapshot = await getDocs(commentsQuery);
      const commentsData = commentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setComments(commentsData);
      
      // Update local comment count
      if (post.commentCount !== undefined) {
        post.commentCount = (post.commentCount || 0) + 1;
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <article className="bg-white dark:bg-gray-800 shadow-sm rounded-xl p-5">
      {/* Breadcrumbs */}
      <div className="mb-2">
        <ul className="inline-flex flex-wrap text-sm font-medium">
          <li className="flex items-center">
            <Link className="text-gray-500 dark:text-gray-400 hover:text-violet-500 dark:hover:text-violet-500" to="/community/forum">
              Forum
            </Link>
            <svg className="fill-current text-gray-400 dark:text-gray-500 mx-2" width="16" height="16" viewBox="0 0 16 16">
              <path d="M6.6 13.4L5.2 12l4-4-4-4 1.4-1.4L12 8z" />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500 dark:text-gray-400">
              {post.stockName || post.stockSymbol}
            </span>
          </li>
        </ul>
      </div>

      {/* Header */}
      <header>
        <div className="flex items-start space-x-3 mb-3">
          <h2 className="text-2xl text-gray-800 dark:text-gray-100 font-bold">{post.title}</h2>
          {/* Upvote button */}
          <button 
            onClick={onUpvote}
            className={`text-xs font-semibold text-center h-12 w-12 border ${
              hasUpvoted 
                ? 'border-violet-500 bg-violet-50 dark:bg-violet-500/10' 
                : 'border-gray-200 dark:border-gray-700/60'
            } hover:border-violet-500 dark:hover:border-violet-500 rounded-lg flex flex-col justify-center items-center`}
          >
            <svg 
              className={`inline-flex ${
                hasUpvoted 
                  ? 'fill-violet-500' 
                  : 'fill-gray-400 dark:fill-gray-500'
              } mt-1.5 mb-1.5`} 
              width="12" 
              height="6" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="m0 6 6-6 6 6z" />
            </svg>
            <div className={hasUpvoted ? 'text-violet-500' : ''}>
              {post.upvotes || 0}
            </div>
          </button>
        </div>

        {/* Meta */}
        <div className="flex flex-wrap text-sm">
          <div className="flex items-center after:block after:content-['·'] last:after:content-[''] after:text-sm after:text-gray-400 dark:after:text-gray-600 after:px-2">
            <img 
              className="rounded-full w-6 h-6 mr-2" 
              src={post.authorPhotoURL || UserAvatar} 
              alt={post.authorName} 
            />
            <span className="font-medium text-violet-500">{post.authorName}</span>
          </div>
          <div className="flex items-center after:block after:content-['·'] last:after:content-[''] after:text-sm after:text-gray-400 dark:after:text-gray-600 after:px-2">
            <span className="text-gray-500">
              {new Date(post.timestamp?.toDate()).toLocaleDateString()}
            </span>
          </div>
         
        </div>
      </header>

      {/* Post Content */}
      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl p-5 mb-4 mt-4">
        <div 
          ref={contentRef}
          className={`text-gray-800 dark:text-gray-100 transition-all duration-200 max-w-2xl mx-auto ${
            !isExpanded ? 'max-h-48' : 'max-h-[600px]'
          } ${isExpanded ? 'overflow-y-auto' : 'overflow-hidden'}`}
        >
          <div className="whitespace-pre-wrap break-words text-base leading-relaxed">
            {post.content}
          </div>
        </div>
        {isOverflowing && (
          <div className="mt-2 text-center border-t dark:border-gray-700 pt-2">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="inline-flex items-center text-sm font-medium text-violet-500 hover:text-violet-600"
            >
              {isExpanded ? 'Vis mindre' : 'Vis mere'}
              <svg 
                className={`ml-1 transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} 
                width="12" 
                height="6" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="m0 0 6 6 6-6z" fill="currentColor"/>
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Comments */}
      <div className="mt-8 border-t dark:border-gray-700 pt-8">
        {user && (
          <form onSubmit={handleSubmitComment} className="mb-6">
            <div className="flex items-start space-x-3">
              <img 
                className="rounded-full shrink-0" 
                src={user.photoURL || UserAvatar} 
                width="40" 
                height="40" 
                alt={user.displayName} 
              />
              <div className="grow">
                <textarea
                  className="form-textarea w-full focus:border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                  rows="4"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Skriv en kommentar..."
                />
              </div>
            </div>
            <div className="text-right mt-3">
              <button 
                type="submit" 
                className="btn-sm bg-violet-500 hover:bg-violet-600 text-white"
                disabled={!newComment.trim() || isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sender...
                  </div>
                ) : (
                  'Send kommentar'
                )}
              </button>
            </div>
          </form>
        )}

        <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-4">Kommentarer</h3>
        <ul className="space-y-5">
          {comments.map((comment) => (
            <li key={comment.id} className="relative pl-9 space-y-5">
              <div className="flex items-start">
                <div className="absolute top-0 left-0">
                  <button 
                    onClick={() => handleCommentUpvote(comment.id)}
                    className={`text-xs font-semibold text-left w-6 rounded-sm flex flex-col justify-center items-center ${
                      commentUpvotes[comment.id] 
                        ? 'text-violet-500' 
                        : 'text-gray-600 dark:text-gray-300 hover:text-violet-500'
                    }`}
                  >
                    <svg className={`inline-flex ${
                      commentUpvotes[comment.id] 
                        ? 'fill-violet-500' 
                        : 'fill-gray-400 dark:fill-gray-500'
                    } mt-1.5 mb-1.5`} width="12" height="6" xmlns="http://www.w3.org/2000/svg">
                      <path d="m0 6 6-6 6 6z" />
                    </svg>
                    <div>{comment.upvotes || 0}</div>
                  </button>
                </div>
                <div className="grow">
                  <div className="grow text-sm text-gray-800 dark:text-gray-100 space-y-2 mb-2">
                    <p>{comment.content}</p>
                  </div>
                  <div className="flex flex-wrap text-xs">
                    <div className="flex items-center after:block after:content-['·'] last:after:content-[''] after:text-sm after:text-gray-400 dark:after:text-gray-600 after:px-2">
                      <img 
                        className="rounded-full w-6 h-6 mr-2" 
                        src={comment.authorPhotoURL || UserAvatar} 
                        alt={comment.fullName || 'Anonymous'} 
                      />
                      <span className="font-medium text-violet-500">{comment.fullName || 'Anonymous'}</span> {/* Updated to use fullName */}
                    </div>
                    <div className="flex items-center after:block after:content-['·'] last:after:content-[''] after:text-sm after:text-gray-400 dark:after:text-gray-600 after:px-2">
                      <span className="text-gray-500">
                        {new Date(comment.timestamp?.toDate()).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <button 
                        onClick={() => handleReply(comment.id, comment.fullName || 'Anonymous')} // Updated to use fullName
                        className="font-medium text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
                      >
                        Svar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Reply form */}
              {replyTo?.id === comment.id && (
                <form onSubmit={handleSubmitReply} className="mt-3">
                  <textarea
                    className="form-textarea w-full focus:border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                    rows="2"
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="Skriv et svar..."
                  />
                  <div className="flex justify-end mt-2 space-x-2">
                    <button
                      type="button"
                      onClick={() => setReplyTo(null)}
                      className="btn-sm border-gray-200 hover:border-gray-300 text-gray-600"
                    >
                      Annuller
                    </button>
                    <button
                      type="submit"
                      className="btn-sm bg-violet-500 hover:bg-violet-600 text-white"
                    >
                      Send svar
                    </button>
                  </div>
                </form>
              )}
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}

export default ForumEntry;